import { useNavigate } from "react-router-dom";
import { useAuth } from "../../modules/auth/context/AuthContext";
import { isIos, isStandalonePwa } from "../../utils/pwaInstall";
import Section from "../Sections";
import Container from "../Container";
import {
  HomeWrapper,
  HeroSection,
  SparkLogo,
  SparkExpansion,
  SparkDescription,
  SparkTagline,
  WelcomeBanner,
  WelcomeBannerInfo,
  Greeting,
  UserBadge,
  SectionTitle,
  AppsGrid,
  AppCard,
  AppIcon,
  AppName,
  AppDescription,
  ValuePropsGrid,
  PropCard,
  PropIcon,
  PropTitle,
  PropText,
  LoginCta,
  DownloadCta,
  PwaInstallHint,
  LoadingState,
  EmptyState,
} from "./Home.module";

// ── Constants ──

const SPARK_EXPANDED = "System for Processes, Actions, Resources & Knowledge";
const SPARK_DESCRIPTION = "Система процесів та корпоративних знань";
const SPARK_TAGLINE = "Центральний хаб для всіх корпоративних операцій";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3090';

// REACT_APP_API_URL уже содержит префикс /api (dev: http://localhost:3090/api,
// prod: /api из compose), поэтому путь к installer без /api — иначе /api/api/...
const DOWNLOAD_URL = `${API_URL}/desktop/installer`;

// MSI-інсталятор — тільки для Windows, на Android кнопка не потрібна
const isAndroid = /Android/i.test(navigator.userAgent);
const showIosInstallHint = isIos() && !isStandalonePwa();

// Маршрути додатків: slug → frontend route
const APP_ROUTES = {
  warehouse: '/warehouse',
  shipments: '/shipments',
  phone_expenses: '/phone-expenses',
  payments: '/payments',
  telephony: '/telephony',
  admin: '/admin',
};

// Описи модулів для карток (статичні, бо backend не повертає description)
const APP_DESCRIPTIONS = {
  warehouse: 'Управління складом: заявки, комплектація, відвантаження, переміщення',
  shipments: 'Управління відправками Нової Пошти: посилки, пакети, друк документів, бухгалтерія',
  phone_expenses: 'Облік рахунків за мобільний/фіксований зв\'язок: завантаження, розпізнавання, оплата',
  payments: 'Пакети платежів: збір рахунків до сплати, формування CSV для клієнт-банку',
  telephony: 'Звернення клієнтів від дзвінка до відвантаження: воронка продажу, замовлення prom.ua, журнал дзвінків',
  admin: 'Адміністрування системи: користувачі, довідники, налаштування доступів',
};

// Дефолтні модулі для ADMIN (коли БД ще не заповнена seed-скриптами)
const ADMIN_DEFAULT_APPS = [
  { slug: 'warehouse', name: 'Склад', icon: '🏭' },
  { slug: 'shipments', name: 'Відправлення', icon: '🚚' },
  { slug: 'phone_expenses', name: 'Связь', icon: '📱' },
  { slug: 'payments', name: 'Платежі', icon: '💳' },
  { slug: 'telephony', name: 'Продажі', icon: '🤝' },
  { slug: 'admin', name: 'Адміністрування', icon: '⚙️' },
];

// Список переваг для неавторизованих
const VALUE_PROPS = [
  {
    icon: '🏭',
    title: 'Складський облік',
    text: 'Заявки на комплектацію, відвантаження, переміщення між складами',
  },
  {
    icon: '📋',
    title: 'Управління заявками',
    text: 'Створення, статуси, виконавці, історія змін — повний цикл',
  },
  {
    icon: '👥',
    title: 'Співпраця в команді',
    text: 'Розподіл ролей, призначення виконавців, контроль задач',
  },
  {
    icon: '🔐',
    title: 'Гнучке розмежування прав',
    text: 'Ролі, посади, підрозділи — точне налаштування доступу',
  },
];

// ── Component ──

const Home = () => {
  const { user, apps, isLoading } = useAuth();
  const navigate = useNavigate();

  // Порядок модулів визначає back (App.sortOrder), фронт лише виводить як є.
  // Для ADMIN, якщо БД ще не заповнена seed-скриптами, дописуємо дефолтні
  // модулі в кінець (не переставляючи ті, що вже прийшли з бека).
  const displayApps = user?.globalRole === 'ADMIN'
    ? [...apps, ...ADMIN_DEFAULT_APPS.filter((d) => !apps.some((a) => a.slug === d.slug))]
    : apps;

  // Стан завантаження
  if (isLoading) {
    return (
      <HomeWrapper>
        <LoadingState>Завантаження…</LoadingState>
      </HomeWrapper>
    );
  }

  return (
    <HomeWrapper>
      {/* ── Hero / SPARK Branding ── */}
      <Section>
        <Container>
          <HeroSection>
            <SparkLogo>SPARK</SparkLogo>
            <SparkExpansion>{SPARK_EXPANDED}</SparkExpansion>
            <SparkDescription>{SPARK_DESCRIPTION}</SparkDescription>
            {!user && <SparkTagline>{SPARK_TAGLINE}</SparkTagline>}
          </HeroSection>
        </Container>
      </Section>

      {showIosInstallHint && (
        <Section>
          <Container>
            <PwaInstallHint>
              📱 Щоб встановити SPARK на iPhone: у Safari натисніть
              «Поділитися» → «На екран “Додому”».
            </PwaInstallHint>
          </Container>
        </Section>
      )}

      {user ? (
        /* ── Авторизований користувач ── */
        <>
          <Section>
            <Container>
              <WelcomeBanner>
                <WelcomeBannerInfo>
                  <Greeting>
                    👋 Вітаємо, {user.firstName || user.authEmail}!
                  </Greeting>
                  {user.departmentName && (
                    <UserBadge>🏢 {user.departmentName}</UserBadge>
                  )}
                </WelcomeBannerInfo>
                {!isAndroid && (
                  <DownloadCta href={DOWNLOAD_URL}>
                    ⬇ Завантажити для Windows
                  </DownloadCta>
                )}
              </WelcomeBanner>
            </Container>
          </Section>

          <Section>
            <Container>
              {displayApps.length > 0 ? (
                <>
                  <SectionTitle>Ваші модулі</SectionTitle>
                  <AppsGrid>
                    {displayApps.map((app) => (
                      <AppCard
                        key={app.slug}
                        onClick={() =>
                          navigate(APP_ROUTES[app.slug] || `/${app.slug}`)
                        }
                      >
                        <AppIcon>{app.icon || '🧩'}</AppIcon>
                        <AppName>{app.name}</AppName>
                        <AppDescription>
                          {APP_DESCRIPTIONS[app.slug] || ''}
                        </AppDescription>
                      </AppCard>
                    ))}
                  </AppsGrid>
                </>
              ) : (
                <EmptyState>
                  У вас поки що немає доступу до жодного модуля.
                  <br />
                  Зверніться до адміністратора для налаштування прав.
                </EmptyState>
              )}
            </Container>
          </Section>
        </>
      ) : (
        /* ── Неавторизований користувач ── */
        <Section>
          <Container>
            <ValuePropsGrid>
              {VALUE_PROPS.map((prop, i) => (
                <PropCard key={i}>
                  <PropIcon>{prop.icon}</PropIcon>
                  <PropTitle>{prop.title}</PropTitle>
                  <PropText>{prop.text}</PropText>
                </PropCard>
              ))}
            </ValuePropsGrid>

            <div style={{ textAlign: 'center' }}>
              <LoginCta href={`${API_URL}/auth/login`}>
                Увійти в систему
              </LoginCta>
            </div>
          </Container>
        </Section>
      )}
    </HomeWrapper>
  );
};

export default Home;
